import { Injectable, NgZone, Inject } from '@angular/core';

import {
    control as MapControl,
    divIcon as CreateDivIcon,
    icon as CreateIcon,
    map as LeafletMap,
    marker as CreateMarker,
    polyline as DrawPolyline,
    popup as CreatePopup,
    tileLayer,
    Control,
    DivIcon,
    FitBoundsOptions,
    Icon,
    IconOptions,
    LatLng,
    LatLngBoundsExpression,
    LatLngExpression,
    LatLngLiteral,
    Layer,
    LayerGroup,
    Map as LeafletMapType,
    MapOptions,
    Marker,
    MarkerClusterGroup,
    MarkerOptions,
    Polyline,
    PolylineOptions,
    Popup,
    PopupOptions,
    TileLayer,
    DomUtil,
    DomEvent,
    LatLngBounds,
    MarkerCluster,
    ZoomPanOptions
} from 'leaflet';
import 'leaflet.locatecontrol';
import 'leaflet.markercluster';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { getMapSettings, setMapSettings } from '@ridango/components/lib/localStorage';

import { MapApiService } from './map-api.service';
import {
    ClusterStylingOptions,
    LayerStyles,
    LayerStylesConfig,
    LayerStylesTranslations,
    LayerStylesValues,
    MapLayersSettingsOption
} from './map.model';
import { patchLeafletMapLocateMethodIfIsNativeApp } from './leaflet-locate-override-for-native-platform';

import { MatDialog } from '@angular/material/dialog';
import { MapSettingsDialogComponent } from '../component/map-settings-dialog/map-settings-dialog.component';
import Env from '@ridango/core/env';
import L from 'leaflet';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class LeafletApiService implements MapApiService {
    private _map: BehaviorSubject<LeafletMapType> = new BehaviorSubject(null);

    private _mapReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private _mapControls: Control.Layers = this.zone.runOutsideAngular(() =>
        MapControl.layers(null, null, { position: 'topright' })
    );

    private _mapOverlays: BehaviorSubject<MapLayersSettingsOption[]> = new BehaviorSubject<MapLayersSettingsOption[]>(
        []
    );
    private _mapBaseLayers: MapLayersSettingsOption[] = [];

    private previousMapBaseLayer;

    private locateLayer: Control.Locate;

    private bounds: LatLngBounds;

    private centeringEnabled = false;

    //If separate token is set for app then use it otherwise this default hardcoded key is used.
    private _accessToken = this.env.LEAFLET_ACCESS_TOKEN
        ? this.env.LEAFLET_ACCESS_TOKEN
        : 'pk.eyJ1IjoicmlkYW5nbyIsImEiOiJlSmwtYUM0In0.15USY6F_8AwcxxKyQa_DWQ';

    private resizeObserver: ResizeObserver;
    private resizing = false;

    nativeMap = () => this._map.asObservable();

    mapReady = () => this._mapReady.asObservable();

    mapOverlays = () => this._mapOverlays.asObservable();

    constructor(
        @Inject('ENV') public env: Env,
        private zone: NgZone,
        private translate: TranslateService,
        private dialog: MatDialog
    ) {}

    private createTiles(style: LayerStylesValues): TileLayer {
        return tileLayer(
            `https://api.mapbox.com/styles/v1/mapbox/${style}/tiles/{z}/{x}/{y}?access_token=${this._accessToken}`,
            {
                attribution:
                    '© <a href="https://apps.mapbox.com/feedback/">Mapbox</a> © ' +
                    '<a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                tileSize: 512,
                zoomOffset: -1,
                updateWhenIdle: true,
                updateWhenZooming: false,
                maxNativeZoom: 20,
                minNativeZoom: 0,
                keepBuffer: 2
            }
        );
    }

    public isCenteringEnabled(): boolean {
        return this.centeringEnabled;
    }

    public setCenteringEnabled(enabled: boolean): void {
        this.centeringEnabled = enabled;
    }

    createMap(
        elementId = 'map',
        options?: MapOptions,
        hiddenMap?: boolean,
        mapStyles: LayerStylesConfig[] = [LayerStylesConfig.Streets],
        hasViewSettingsControl?: boolean
    ): L.Map {
        const map = LeafletMap(elementId, {
            ...(options || {}),
            bounceAtZoomLimits: false,
            zoomSnap: 1,
            zoomDelta: 2
        });

        const layers = mapStyles.map((el) => ({
            name: el,
            label: LayerStylesTranslations[el],
            layer: this.createTiles(LayerStyles[el])
        }));

        const configuredBaseLayer = getMapSettings()?.baseLayer || LayerStylesConfig.Streets;
        const defaultLayer = layers.find((layer) => layer.name === configuredBaseLayer) || layers[0];
        map.addLayer(defaultLayer?.layer);

        patchLeafletMapLocateMethodIfIsNativeApp(map);

        if (!hiddenMap && hasViewSettingsControl) {
            this.customViewSettingsControl().addTo(map);
        }

        if (this.env.MAP?.MAX_BOUNDS) {
            this.bounds = L.latLngBounds(...this.env.MAP.MAX_BOUNDS);
            map.setMaxBounds(this.bounds);
            map.setMinZoom(map.getBoundsZoom(this.bounds));
        }

        this._mapBaseLayers = layers;
        this._map.next(map);
        this._mapReady.next(true);

        const mapContainer = document.getElementById(elementId);
        if (mapContainer) {
            this.resizeObserver = new ResizeObserver(() => {
                if (!this.resizing) {
                    this.resizing = true;
                    requestAnimationFrame(() => {
                        this.doResize();
                        this.resizing = false;
                    });
                }
            });
            this.resizeObserver.observe(mapContainer);
        }

        return map;
    }

    addStopsToMap(): void {
        const overlays = this._mapOverlays.getValue().filter(({ name, label }) => name && label);
        const stops = overlays.find((item) => item.name === 'schedule-stops');

        if (stops && stops.layer) {
            this.addListOfLayers([stops.layer]);
        }
    }

    removeStopsFromMap(): void {
        const overlays = this._mapOverlays.getValue().filter(({ name, label }) => name && label);
        const stops = overlays.find((item) => item.name === 'schedule-stops');

        if (stops && stops.layer) {
            this.removeLayer(stops.layer);
        }
    }

    openSettingsDialog(event): void {
        if (this.env.IS_MOBILE_APP && this.env.JOURNEY_PLANNER?.SHOW_FULL_MAP) {
            event.stopPropagation();
        }
        const overlays = this._mapOverlays.getValue().filter(({ name, label }) => name && label);
        const layers = this._mapBaseLayers;
        const data = {
            layers,
            overlays,
            selectedLayer: this.previousMapBaseLayer
                ? layers.find((el) => el.name === this.previousMapBaseLayer.name)
                : layers.find((el) => this.hasLayer(el.layer)),
            hiddenOverlays: overlays.filter((el) => !this.hasLayer(el.layer)).map(({ name }) => name)
        };

        this.zone.run(() => {
            this.dialog
                .open(MapSettingsDialogComponent, {
                    data,
                    maxWidth: '600px',
                    width: '90%',
                    restoreFocus: false
                })
                .afterClosed()
                .pipe(take(1), filter(Boolean))
                .subscribe((result: { baseLayer: MapLayersSettingsOption; hiddenOverlays: string[] }) => {
                    const { baseLayer, hiddenOverlays } = result;
                    setMapSettings({
                        baseLayer: baseLayer.name,
                        hiddenOverlays
                    });

                    if (this.previousMapBaseLayer) {
                        this.removeLayer(this.previousMapBaseLayer.layer);
                        this.previousMapBaseLayer = baseLayer;
                    } else {
                        this.previousMapBaseLayer = baseLayer;
                    }

                    const removedLayers = [];
                    const addedLayers = [baseLayer.layer];

                    overlays.forEach(({ name, layer }) => {
                        if (hiddenOverlays.includes(name)) {
                            removedLayers.push(layer);
                        } else {
                            addedLayers.push(layer);
                        }
                    });

                    this.removeListOfLayers(removedLayers);
                    this.addListOfLayers(addedLayers);
                });
        });
    }

    destroyMap(): void {
        const map = this._map.getValue();

        if (map) {
            map.off();
            map.eachLayer((layer: any) => {
                if (layer.off) {
                    layer.off();
                }
                if (typeof layer.remove === 'function') {
                    layer.remove();
                }
            });

            map.remove();
        }

        this._map.next(null);
        this._mapReady.next(false);

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    addToMap(layer: Layer | Control.Layers): void {
        const map = this._map.getValue();
        if (!map) {
            throw new Error(
                'Map is not ready. Should subscribe to mapReady() and call this method within the subscription'
            );
        }
        layer.addTo(map);
    }

    drawPolyline(latlngs: LatLngExpression[] | LatLngExpression[][], options?: PolylineOptions): Polyline {
        return DrawPolyline(latlngs, options);
    }

    createPopup(content: any, options: PopupOptions): Popup {
        const popup = CreatePopup(options);

        return popup.setContent(content);
    }

    openPopup(latlng: LatLngLiteral, popup: Popup): void {
        const map = this._map.getValue();
        popup.setLatLng(latlng).openOn(map);
    }

    closePopup(): void {
        const map = this._map.getValue();
        if (map) {
            map.closePopup();
        }
    }

    bindPopupToLayer(layer: Layer, popup: Popup): void {
        layer.bindPopup(popup);
    }

    createMarker(latlng: LatLngLiteral, options: MarkerOptions = null): Marker {
        return CreateMarker(latlng, options);
    }

    setMarkerIcon(marker: Marker, icon: Icon | DivIcon): void {
        marker.setIcon(icon);
    }

    addPopupToMarker(marker: Marker, content: any): void {
        marker.bindPopup(content);
    }

    createIcon(options: IconOptions, divIcon = false): Icon | DivIcon {
        return divIcon ? CreateDivIcon(options) : CreateIcon(options);
    }

    createLayerGroup(): LayerGroup {
        return new LayerGroup([]);
    }

    clearLayerGroup(layerGroup: LayerGroup): void {
        layerGroup.clearLayers();
    }

    removeLayerGroup(layerGroup: LayerGroup): void {
        layerGroup.clearLayers();
        this.removeLayer(layerGroup);
    }

    addToLayerGroup(layerGroup: LayerGroup, layer: Layer): void {
        layer.addTo(layerGroup);
    }

    removeFromLayerGroup(layerGroup: LayerGroup, layer: Layer): void {
        layerGroup.removeLayer(layer);
    }

    applyLayers(layers: Layer[]): void {
        const map = this._map.getValue();
        if (!map) {
            throw new Error(
                'Map is not ready. Should subscribe to mapReady() and call this method within the subscription'
            );
        }

        map.eachLayer((layer) => {
            if (this.locateLayer['_circle']) {
                const locateCircleId = this.locateLayer['_circle']['_leaflet_id'];
                const isContainLocationIcon = layer['_locationIcon'];
                const isContainLocationCircleLayer = layer['_layers'] && layer['_layers'][locateCircleId];

                if (isContainLocationCircleLayer || isContainLocationIcon) {
                    return;
                }
            }
            map.removeLayer(layer);
        });

        layers.forEach((layer) => map.addLayer(layer));
        this._map.next(map);
    }

    addListOfLayers(layers: Layer[]): void {
        const map = this._map.getValue();
        if (!map) {
            throw new Error(
                'Map is not ready. Should subscribe to mapReady() and call this method within the subscription'
            );
        }

        layers.forEach((layer) => map.addLayer(layer));
        this._map.next(map);
    }

    hasLayer(layer: Layer): boolean {
        const map = this._map.getValue();
        return map.hasLayer(layer);
    }

    removeListOfLayers(layers: Layer[]): void {
        layers.forEach((layer) => this.removeLayer(layer));
    }

    removeLayer(layer: Layer): void {
        layer?.remove();
    }

    createMarkerClusterGroup(options = {}): MarkerClusterGroup {
        return new MarkerClusterGroup(options);
    }

    //custom marker cluster styling function (use iconCreateFunction on createMarkerClusterGroup options to create custom icon if it needed)
    createCustomClusterMarkers(cluster: MarkerCluster, stylingOptions: ClusterStylingOptions = {}): DivIcon {
        const count = cluster.getChildCount();
        const className = `marker-cluster ${stylingOptions.containerClass || ''} `;
        const wrapperClass = stylingOptions.wrapperClass || '';
        const spanClass = stylingOptions.spanClass || '';
        return new DivIcon({
            html: `<div class="${wrapperClass}"><span class='${spanClass}'>${count}</span></div>`,
            className: className,
            iconSize: new L.Point(40, 40)
        });
    }

    customViewSettingsControl(): Control {
        const customControl = Control.extend({
            options: {
                position: 'topright'
            },
            onAdd: () => {
                const container = DomUtil.create(
                    'div',
                    'leaflet-bar leaflet-control leaflet-control-view-settings-container'
                );
                const styleClass =
                    this.env.APP_NAME === 'gotland' && !this.env.IS_MOBILE_APP
                        ? 'leaflet-control-view-settings leaflet-control-view-settings-gotland'
                        : 'leaflet-control-view-settings';

                const button = DomUtil.create('button', styleClass, container);
                button.setAttribute('aria-label', this.translate.instant('MAP_VIEW_SETTING_TITLE'));

                // into a button as span with class leaflet-control-view-settings-icon
                const icon = DomUtil.create('span', 'leaflet-control-view-settings-icon', button);
                button.appendChild(icon);
                DomEvent.disableClickPropagation(button);
                DomEvent.on(button, 'click', this.openSettingsDialog.bind(this));

                return container;
            }
        });

        return new customControl();
    }

    addToOverlays(name: string, label: string, layerGroup: Layer): void {
        const currentOverlays = this._mapOverlays.getValue().filter((v) => v.name !== name);
        this._mapOverlays.next([...currentOverlays, { name, label, layer: layerGroup }]);
    }

    removeFromOverlays(name: string): void {
        this._mapOverlays.next(this._mapOverlays.getValue().filter((el) => el.name !== name));
    }

    addToControls(label: string, layerGroup: LayerGroup): void {
        this._mapControls.addOverlay(layerGroup, label);
    }

    removeFromControls(layerGroup: LayerGroup): void {
        this._mapControls.removeLayer(layerGroup);
    }

    showControls(show: boolean) {
        show ? this.addToMap(this._mapControls) : this._mapControls.remove();
    }

    createClickEventSubscription(): Observable<{ latlng: LatLng }> {
        const map = this._map.getValue();

        return new Observable((observer) => {
            map.addEventListener('click', (e: { latlng: LatLng }) => observer.next(e));
        });
    }

    isWithinMapBounds(latlng: LatLng): boolean {
        const map = this._map.getValue();

        return map?.getBounds().contains(latlng);
    }

    fitBounds(bounds: LatLngBoundsExpression, options?: FitBoundsOptions): void {
        const map = this._map.getValue();
        if (map) {
            map.fitBounds(bounds, options);
        }
    }

    checkIsContainBound(latlng: LatLng) {
        if (!this.env.MAP?.MAX_BOUNDS) {
            return true;
        }

        return this.bounds && this.bounds.contains(latlng);
    }

    doResize(): void {
        // Invalidate the map size to trigger it to update itself
        const map = this._map.getValue();
        if (map) {
            map.invalidateSize();
        }
    }

    zoomIn(): void {
        const map = this._map.getValue();
        if (map) {
            map.zoomIn();
        }
    }

    zoomOut(): void {
        const map = this._map.getValue();
        if (map) {
            map.zoomOut();
        }
    }

    initLocate(options): Control.Locate {
        this.locateLayer = new Control.Locate({ ...options });
        return this.locateLayer;
    }

    getCenterCoordinates(): LatLng {
        const map = this._map.getValue();
        return map.getCenter();
    }

    centerMapToCoordinates(latlng: LatLngExpression, zoom = 16, zoomOptions?: ZoomPanOptions): void {
        const map = this._map.getValue();
        map.setView(latlng, zoom, zoomOptions);
    }

    addListener(event: string, fn: () => void, context?: any): void {
        const map = this._map.getValue();
        if (map) {
            map.on(event, fn, context);
        }
    }

    hasEventListener(event: string): boolean {
        const map = this._map.getValue();
        if (map) {
            return map.hasEventListeners(event);
        } else {
            return false;
        }
    }

    addListenerSubscription(event: string): Observable<any> {
        const map = this._map.getValue();
        if (map) {
            return new Observable((observer) => {
                map.on(event, (evt) => observer.next(evt));
            });
        }
    }

    removeListener(event: string, fn?: () => void, context?: any): void {
        const map = this._map.getValue();
        if (map) {
            map.off(event, fn, context);
        }
    }

    removeAllListeners(): void {
        const map = this._map.getValue();
        if (map) {
            map.off();
        }
    }

    addClass(el: HTMLElement, className: string): void {
        DomUtil.addClass(el, className);
    }

    removeClass(el: HTMLElement, className: string): void {
        DomUtil.removeClass(el, className);
    }

    public getNativeMap(): LeafletMapType {
        return this._map.getValue();
    }

    addListenerWithEventCallback(event: string, fn: (event: any) => void): void {
        const map = this._map.getValue();
        if (map) {
            map.on(event, fn);
        }
    }

    once(event: string, fn: (event?: any) => void, context?: any): void {
        const map = this._map.getValue();
        if (map) {
            map.once(event, fn, context);
        }
    }
}
